import { Alert, Loading, PageHeader, Pagination, TextInput } from "@vellum/ui";
import type { ChangeEvent, ReactNode } from "react";

type ListPageProps = {
  title: string;
  subtitle: string;
  query: string;
  onQueryChange: (value: string) => void;
  searchPlaceholder: string;
  /** Rendered to the right of the search box, e.g. a company filter. */
  filters?: ReactNode;
  /** Initial load only — full-page spinner. */
  loading: boolean;
  /** Any in-flight request — keep the table, dim it slightly. */
  isFetching?: boolean;
  error: string | null;
  /** 0-based page index. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  children: ReactNode;
};

/** Shared chrome for the admin list pages: header, search, filters, states. */
export function ListPage({
  title,
  subtitle,
  query,
  onQueryChange,
  searchPlaceholder,
  filters,
  loading,
  isFetching = false,
  error,
  page,
  pageSize,
  total,
  onPageChange,
  children,
}: ListPageProps) {
  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextInput
          label="Search"
          value={query}
          placeholder={searchPlaceholder}
          containerClassName="w-full sm:max-w-xs"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onQueryChange(event.target.value)
          }
        />
        {filters}
      </div>

      {error && <Alert className="mb-4">{error}</Alert>}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loading />
        </div>
      ) : (
        <>
          <div
            className={
              isFetching
                ? "pointer-events-none opacity-60 transition-opacity"
                : "transition-opacity"
            }
            aria-busy={isFetching}
          >
            {children}
          </div>
          {!error && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={onPageChange}
              disabled={isFetching}
            />
          )}
        </>
      )}
    </div>
  );
}
